<script setup lang="ts">
import type { IconDefinition } from '@fortawesome/fontawesome-common-types'
import { faArrowRight, faBooks, faFilePen, faMagnifyingGlass, faSection } from '@fortawesome/pro-duotone-svg-icons'
import { TabsIndicator, TabsList, TabsRoot, TabsTrigger } from 'reka-ui'
import { getSrc } from '~/utils/crystallize/tutorial-video'

definePageMeta({
  name: 'tutorial-videos',
  theme: 'carbon',
  colorMode: 'light',
  layout: false,
})

const { data, suspense } = useTutorialVideos()

await suspense()

// Icons for known themes. If there are more themes added in Crystallize, they should work, but will not have icons.
const ICON_MAPPING: Record<string, IconDefinition> = {
  laws: faSection,
  literature: faBooks,
  templates: faFilePen,
  functionality: faMagnifyingGlass,
}

const route = useRoute()

const theme = ref('all')

watch(theme, (newTheme) => {
  const { theme: _oldTheme, ...query } = route.query
  if (newTheme === 'all') {
    navigateTo({ query })
  }
  else {
    navigateTo({ query: { ...query, theme: newTheme } })
  }
})

// Only show themes that there are videos for
const filtered = computed(() => {
  const validThemes: Array<{ value: string, label: string, icon?: IconDefinition }> = [
    {
      value: 'all',
      label: 'Se\xA0alle',
      icon: faArrowRight,
    },
  ]

  const filteredVideos = []

  const allVideos = data.value?.children
  if (allVideos?.length) {
    for (const video of allVideos) {
      if (!getSrc(video)) {
        continue
      }

      const videoTheme = video.components?.theme?.[0]

      // If the video has a theme that is not yet in the validThemes list, add it
      if (videoTheme && !validThemes.some(t => t.value === videoTheme.value)) {
        validThemes.push({
          value: videoTheme.value,
          label: videoTheme.key,
          icon: ICON_MAPPING[videoTheme.value],
        })
      }

      // If the video's theme matches the selected theme, or if "all" is selected, include the video in the filtered list
      if (theme.value === 'all' || videoTheme?.value === theme.value) {
        filteredVideos.push(video)
      }
    }
  }

  return {
    themes: validThemes,
    videos: filteredVideos,
  }
})

const title = computed(() => data.value?.components?.title)

useSeoMeta({
  title,
  description: computed(() => getTextContent(data.value?.components?.description)),
})

useHead({
  bodyAttrs: {
    // Store the background color in a CSS variable, as it's reused for the gradient in the scrollable Chapters component
    class: '[--bg-color:theme(colors.carbon.50)] bg-[--bg-color]',
  },
})
</script>

<template>
  <NuxtLayout name="nettbutikk" :show-breadcrumbs="false">
    <ContentGrid>
      <header class="full-width-content relative grid grid-cols-subgrid pb-m md:pb-3xl md:pt-xl">
        <Shape variant="circle" class="h-full hidden md:block absolute -left-[30%] fill-moss-200" />

        <hgroup class="flex flex-col gap-s md:gap-m relative full-width lg:col-span-6 lg:ml-12">
          <h1 v-if="title" v-text="title" />
          <CrystallizeRichText v-for="(block, i) in data?.components?.description" :key="i" class="title-s !font-light" :block="block" />
        </hgroup>
      </header>

      <section v-if="filtered.videos.length" class="full-width grid grid-cols-subgrid gap-y-3 lg:gap-y-8 items-center mb-xl">
        <div class="flex full-width-content gap-x-xl gap-y-3xs items-center flex-wrap lg:ml-12">
          <b class="title-xs md:title-m">Velg tema:</b>

          <TabsRoot v-model="theme" class="hidden lg:block" orientation="horizontal" activation-mode="manual">
            <TabsList class="flex gap-2xs items-center relative">
              <TabsIndicator
                class="absolute transition-all h-full w-[--reka-tabs-indicator-size] translate-x-[--reka-tabs-indicator-position]"
              >
                <div class="size-full bg-blurple-200 rounded-lg" />
              </TabsIndicator>
              <TabsTrigger
                v-for="({ value, label, icon }) in filtered.themes"
                :key="value"
                :value="value"
                class="flex gap-2xs items-center px-s py-2.5 z-10 rounded-lg hover:bg-blurple-200 transition-colors"
              >
                <Icon v-if="icon" :icon="icon" />
                {{ label }}
              </TabsTrigger>
            </TabsList>
          </TabsRoot>

          <FormSelect
            v-model="theme"
            intent="filled"
            :options="filtered.themes"
            class="lg:hidden"
          >
            <template #selected="{ option }">
              <div class="flex items-center gap-s">
                <Icon v-if="option?.icon" :icon="option.icon" />
                {{ option?.label }}
              </div>
            </template>

            <template #option="{ option }">
              {{ option.label }}
            </template>
          </FormSelect>
        </div>

        <ul v-auto-animate class="full-width grid grid-cols-1 lg:grid-cols-2 gap-y-8 gap-x-6">
          <TutorialVideoCard
            v-for="video in filtered.videos"
            :key="video.id"
            :video="video"
          />
        </ul>
      </section>

      <NuxtPage />
    </ContentGrid>
  </NuxtLayout>
</template>
